import { fetchToken } from "../apis/token-api";

const TOKEN_PAYLOAD_KEY = "portal_token_payload";

export const SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Faça login novamente.";
export const AUTH_SERVER_UNAVAILABLE_MESSAGE =
  "Não foi possível renovar a sessão: servidor de autenticação indisponível. Tente novamente em instantes.";
export const LICENSE_UNAVAILABLE_MESSAGE =
  "Não existe licença disponível no License Server para atender a requisição nesse momento.";

let latestAccessToken = null;
/** Tokens já substituídos por uma renovação; chamadas que ainda os usam recebem o atual. */
const staleTokens = new Set();
let refreshPromise = null;
let sessionExpiredNotified = false;
const refreshListeners = new Set();
const expiredListeners = new Set();

function readStoredAccessToken() {
  try {
    const raw = localStorage.getItem(TOKEN_PAYLOAD_KEY);
    return raw ? JSON.parse(raw)?.access_token || null : null;
  } catch {
    return null;
  }
}

export function resolveAccessToken(token) {
  if (token && !staleTokens.has(token)) return token;
  return latestAccessToken || token || readStoredAccessToken();
}

export function onWorkflowTokenRefreshed(listener) {
  refreshListeners.add(listener);
  return () => refreshListeners.delete(listener);
}

export function onWorkflowSessionExpired(listener) {
  expiredListeners.add(listener);
  return () => expiredListeners.delete(listener);
}

export function resetWorkflowSession() {
  latestAccessToken = null;
  staleTokens.clear();
  refreshPromise = null;
  sessionExpiredNotified = false;
}

function isAuthRejection(error) {
  return (
    Boolean(error?.isPasswordChangeRequired) ||
    [400, 401, 403].includes(Number(error?.status))
  );
}

function notifySessionExpired(error) {
  if (sessionExpiredNotified) return;
  sessionExpiredNotified = true;
  const message = error?.isPasswordChangeRequired
    ? error.userMessage
    : SESSION_EXPIRED_MESSAGE;
  expiredListeners.forEach((listener) => listener({ message, error }));
}

/**
 * Renova o token uma única vez, mesmo com várias chamadas recebendo 401 ao mesmo tempo.
 * Rejeição de credenciais encerra a sessão; falha de rede/servidor não.
 */
export function refreshWorkflowToken(failedToken, { username, password } = {}) {
  if (
    failedToken &&
    latestAccessToken &&
    failedToken !== latestAccessToken &&
    staleTokens.has(failedToken)
  ) {
    return Promise.resolve(latestAccessToken);
  }

  if (!refreshPromise) {
    const user = username || localStorage.getItem("portal_username") || "";
    const pass = password || localStorage.getItem("portal_password") || "";

    refreshPromise = fetchToken(user, pass)
      .then((payload) => {
        if (failedToken) staleTokens.add(failedToken);
        latestAccessToken = payload.access_token;
        localStorage.setItem(TOKEN_PAYLOAD_KEY, JSON.stringify(payload));
        refreshListeners.forEach((listener) => listener(payload));
        return payload.access_token;
      })
      .catch((error) => {
        if (isAuthRejection(error)) {
          notifySessionExpired(error);
          error.sessionExpired = true;
          error.userMessage = error.userMessage || SESSION_EXPIRED_MESSAGE;
        } else {
          error.userMessage = AUTH_SERVER_UNAVAILABLE_MESSAGE;
        }
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}
